import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { formatDetail } from "@/utils/formatDetail";
import { Loader2, Plus, Trash2, Calculator, Save, FileText, X } from "lucide-react";
import { NumStepper } from "@/components/ui/num-stepper";
import { QuotationSheet } from "@/components/QuotationSheet";

const fmt = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

const EMPTY_ITEM = {
  description: "GI Perforated Cable Tray",
  size_idx: "",
  f1: 50, width: 100, f2: 50,
  thickness: 1.6, thk_mode: "mm", swg: 16,
  length_ft: 8, length_unit: "ft",
  qty: 1,
  perforation_loss: 0, price_format: "per_piece",
};

const FT_PER_M = 3.28084;

// Standard Wire Gauge → mm
const SWG_MM = { 8: 4.064, 9: 3.658, 10: 3.251, 11: 2.946, 12: 2.642, 13: 2.337, 14: 2.032, 15: 1.829, 16: 1.626, 17: 1.422, 18: 1.219, 19: 1.016, 20: 0.914, 21: 0.813, 22: 0.711, 23: 0.61, 24: 0.559 };

const LABEL = "font-mono text-[10px] tracking-[0.2em] uppercase text-white font-semibold";

const NUM_FIELDS = [
  { k: "f1", label: "F1 (mm)" },
  { k: "width", label: "W (mm)" },
  { k: "f2", label: "F2 (mm)" },
];

export const AdminQuoteNew = ({ client, api, goTab, userName }) => {
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([{ ...EMPTY_ITEM }]);
  const [preview, setPreview] = useState(null);
  const [docPreview, setDocPreview] = useState(null);
  const [sizes, setSizes] = useState([]);
  const [meta, setMeta] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await client.get(`${api}/qv2/customers`);
      setCustomers(data);
      const metaRes = await client.get(`${api}/qv2/quote-meta`);
      setMeta(metaRes.data);
      setSizes(metaRes.data.custom_sizes || []);
    } catch {
      toast.error("Could not load customers");
    }
  }, [client, api]);

  const applySize = (i, si) => {
    const next = items.map((it, j) => {
      if (j !== i) return it;
      if (si === "") return { ...it, size_idx: "" };
      const s = sizes[Number(si)];
      return { ...it, size_idx: String(si), f1: s.f1, width: s.width, f2: s.f2 };
    });
    setItems(next);
    setPreview(null);
  };

  useEffect(() => {
    load();
  }, [load]);

  const setItem = (i, k, v) => {
    const next = items.map((it, j) => (j === i ? { ...it, [k]: v } : it));
    setItems(next);
    setPreview(null);
  };

  const payloadItems = () =>
    items.map((it) => {
      const lengthFt = it.length_unit === "m" ? Number(it.length_ft) * FT_PER_M : Number(it.length_ft);
      const lengthM = lengthFt * 0.3048;
      // Qty follows the length unit: ft → pieces, mtrs → meters (converted to pieces for math)
      const qtyPcs = it.length_unit === "m" ? (lengthM > 0 ? Number(it.qty) / lengthM : 0) : Number(it.qty);
      const thkMm = it.thk_mode === "swg" ? SWG_MM[it.swg] : Number(it.thickness);
      return {
        description: it.description,
        f1: Number(it.f1), width: Number(it.width), f2: Number(it.f2),
        thickness: thkMm,
        thickness_label: it.thk_mode === "swg" ? `${it.swg} SWG` : `${Number(it.thickness)} mm`,
        length_ft: Math.round(lengthFt * 10000) / 10000,
        length_unit: it.length_unit,
        qty: Math.round(qtyPcs * 1000) / 1000,
        perforation_loss: Number(it.perforation_loss) || 0,
        price_format: it.price_format,
      };
    });

  const doPreview = async () => {
    setBusy(true);
    try {
      const { data } = await client.post(`${api}/qv2/preview`, { items: payloadItems() });
      setPreview(data);
      return data;
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || "Check item values");
      return null;
    } finally {
      setBusy(false);
    }
  };

  const previewDocument = async () => {
    if (!customerId) {
      toast.error("Select a customer first");
      return;
    }
    const data = await doPreview();
    if (!data) return;
    const payload = payloadItems();
    setDocPreview({
      company: meta?.company,
      terms: meta?.terms || [],
      customer: customers.find((c) => c.id === customerId) || { company: "—" },
      quote_no: "DRAFT PREVIEW",
      created_at: new Date().toISOString(),
      items: payload.map((p, i) => ({ ...p, calc: data.items[i] })),
      subtotal: data.subtotal,
      gst_pct: meta?.gst ?? 18,
      gst_amount: data.gst_amount,
      grand_total: data.grand_total,
      notes,
      created_by: userName,
    });
  };

  const save = async () => {
    if (!customerId) {
      toast.error("Select a customer first");
      return;
    }
    setSaving(true);
    try {
      const { data } = await client.post(`${api}/qv2/quotations`, {
        customer_id: customerId,
        ...(notes && { notes }),
        items: payloadItems(),
      });
      toast.success(`Quotation ${data.quote_no} created`);
      goTab("quotes");
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail, "Could not save quotation"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="new-quote-panel" className="grid grid-cols-1 lg:grid-cols-12 gap-8">
      <div className="lg:col-span-4">
        <div className="bg-steel border border-white/10 p-6 md:p-7">
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke mb-3">Customer</p>
          <select
            data-testid="quote-customer-select"
            value={customerId}
            onChange={(e) => setCustomerId(e.target.value)}
            className="w-full bg-ink border border-white/20 py-3 px-3 font-body text-sm text-white focus:border-[#7FB3E8] focus:outline-none"
          >
            <option value="">Select customer from the list</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>{c.company}{c.contact ? ` (${c.contact})` : ""}</option>
            ))}
          </select>
          {customers.length === 0 && (
            <p className="mt-3 font-mono text-[10px] tracking-widest uppercase text-[#7FB3E8]">
              No customers yet — add one in the Customers tab first
            </p>
          )}
          <p className="mt-6 font-mono text-[10px] tracking-[0.3em] uppercase text-smoke mb-3">Notes (printed on quotation)</p>
          <textarea
            data-testid="quote-notes-input"
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional notes for the customer..."
            className="w-full bg-transparent border-0 border-b border-white/25 py-2.5 font-body text-sm text-white placeholder:text-white/35 focus:border-[#7FB3E8] focus:outline-none resize-none transition-colors duration-300"
          />
          <p className="mt-6 font-body text-xs text-smoke leading-relaxed">
            Sheet rate, overhead, margin and terms are managed in Rates by admin. GST is
            auto-applied. Weight is not shown on the printed quotation.
          </p>
        </div>
      </div>

      <div className="lg:col-span-8">
        <div className="flex items-center justify-between mb-4">
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Line Items — Cable Tray Sizes</p>
          <button data-testid="quote-add-row-button" onClick={() => setItems([...items, { ...EMPTY_ITEM }])} className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#7FB3E8] hover:text-white transition-colors duration-300 flex items-center gap-1">
            <Plus size={13} /> Add Row
          </button>
        </div>

        {items.map((it, i) => (
          <div key={i} data-testid={`quote-item-${i}`} className="bg-steel border border-white/10 p-5 md:p-6 mb-4">
            <div className="flex items-center justify-between mb-4">
              <p data-testid={`quote-item-sno-${i}`} className="font-heading font-bold uppercase tracking-wider text-base text-white">S.No {i + 1}</p>
              {items.length > 1 && (
                <button data-testid={`quote-item-remove-${i}`} onClick={() => setItems(items.filter((_, j) => j !== i))} aria-label="Remove item" className="p-1.5 text-white/50 hover:text-red-400 transition-colors duration-300">
                  <Trash2 size={15} />
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="col-span-2">
                <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-white font-semibold">Standard Size</label>
                <select
                  data-testid={`quote-stdsize-${i}`}
                  value={it.size_idx}
                  onChange={(e) => applySize(i, e.target.value)}
                  className="mt-1 w-full bg-ink border border-white/20 py-2 px-2 font-body text-sm text-white focus:border-[#7FB3E8] focus:outline-none"
                >
                  <option value="">Custom size (enter F1 / W / F2 below)</option>
                  {sizes.map((s, si) => (
                    <option key={si} value={si}>Standard — {s.label} ({s.f1}×{s.width}×{s.f2} mm)</option>
                  ))}
                </select>
                {it.size_idx !== "" && (
                  <p data-testid={`quote-size-locked-${i}`} className="mt-1.5 font-mono text-[10px] tracking-widest uppercase text-[#7FB3E8]">Standard size selected — F1 / W / F2 locked</p>
                )}
              </div>
              <div className="col-span-2">
                <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-white font-semibold">Description</label>
                <input data-testid={`quote-desc-${i}`} value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} className="mt-1 w-full bg-transparent border-0 border-b border-white/25 py-2 font-body text-sm text-white focus:border-[#7FB3E8] focus:outline-none transition-colors duration-300" />
              </div>
              {NUM_FIELDS.map((f) => {
                const locked = it.size_idx !== "" && (f.k === "f1" || f.k === "width" || f.k === "f2");
                return (
                  <NumStepper
                    key={f.k}
                    label={f.label}
                    testid={`quote-${f.k}-${i}`}
                    step={f.step || 1}
                    value={it[f.k]}
                    disabled={locked}
                    onChange={(v) => setItem(i, f.k, v)}
                  />
                );
              })}
              <div>
                <label className={LABEL}>Thickness</label>
                <div className="mt-1 flex items-stretch gap-1.5">
                  {it.thk_mode === "mm" ? (
                    <div className="flex-1">
                      <NumStepper testid={`quote-thickness-${i}`} step={0.1} value={it.thickness} onChange={(v) => setItem(i, "thickness", v)} />
                    </div>
                  ) : (
                    <select
                      data-testid={`quote-swg-${i}`}
                      value={it.swg}
                      onChange={(e) => setItem(i, "swg", Number(e.target.value))}
                      className="flex-1 bg-ink border border-white/20 py-2 px-2 font-body text-sm font-semibold text-white focus:border-[#7FB3E8] focus:outline-none"
                    >
                      {Object.keys(SWG_MM).map((g) => (
                        <option key={g} value={g}>{g} SWG ({SWG_MM[g]} mm)</option>
                      ))}
                    </select>
                  )}
                  <select
                    data-testid={`quote-thk-unit-${i}`}
                    value={it.thk_mode}
                    onChange={(e) => setItem(i, "thk_mode", e.target.value)}
                    className="shrink-0 bg-ink border border-white/20 py-2 px-2 font-body text-sm text-white focus:border-[#7FB3E8] focus:outline-none"
                  >
                    <option value="mm">mm</option>
                    <option value="swg">SWG</option>
                  </select>
                </div>
              </div>
              <NumStepper
                label="Length"
                testid={`quote-length_ft-${i}`}
                step={0.5}
                value={it.length_ft}
                onChange={(v) => setItem(i, "length_ft", v)}
                unit={
                  <select
                    data-testid={`quote-length-unit-${i}`}
                    value={it.length_unit}
                    onChange={(e) => setItem(i, "length_unit", e.target.value)}
                    className="shrink-0 bg-ink border border-white/20 py-2 px-2 font-body text-sm text-white focus:border-[#7FB3E8] focus:outline-none"
                  >
                    <option value="ft">ft</option>
                    <option value="m">mtrs</option>
                  </select>
                }
              />
              <NumStepper
                label={it.length_unit === "m" ? "Qty (mtrs)" : "Qty (pieces)"}
                testid={`quote-qty-${i}`}
                value={it.qty}
                onChange={(v) => setItem(i, "qty", v)}
              />
              <NumStepper
                label="Perforation Loss %"
                testid={`quote-perforation_loss-${i}`}
                value={it.perforation_loss}
                onChange={(v) => setItem(i, "perforation_loss", v)}
              />
              <div>
                <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-white font-semibold">Price Format</label>
                <select data-testid={`quote-format-${i}`} value={it.price_format} onChange={(e) => setItem(i, "price_format", e.target.value)} className="mt-1 w-full bg-ink border border-white/20 py-2 px-2 font-body text-sm text-white focus:border-[#7FB3E8] focus:outline-none">
                  <option value="per_piece">Per piece (length)</option>
                  <option value="per_meter">Per meter</option>
                  <option value="per_kg">Per kg</option>
                </select>
              </div>
            </div>
            {preview?.items?.[i] && (
              <p className="mt-4 font-mono text-xs text-smoke" data-testid={`quote-item-result-${i}`}>
                Sheet wt {preview.items[i].kg_per_piece} kg/pc · {preview.items[i].total_kg} kg total · Unit {fmt(preview.items[i].unit_price)} · Line <span className="text-white">{fmt(preview.items[i].line_total)}</span>
              </p>
            )}
          </div>
        ))}

        <div className="flex flex-wrap items-center gap-4">
          <button data-testid="quote-preview-button" onClick={doPreview} disabled={busy} className="btn-sweep btn-ghost font-heading font-semibold uppercase tracking-wider text-base px-6 py-3 flex items-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Calculator size={16} />} Preview Totals
          </button>
          <button data-testid="quote-doc-preview-button" onClick={previewDocument} disabled={busy} className="btn-sweep btn-ghost font-heading font-semibold uppercase tracking-wider text-base px-6 py-3 flex items-center gap-2 disabled:opacity-60">
            <FileText size={16} /> Preview Quotation
          </button>
          <button data-testid="quote-save-button" onClick={save} disabled={saving} className="btn-sweep btn-solid font-heading font-semibold uppercase tracking-wider text-base px-6 py-3 flex items-center gap-2 disabled:opacity-60">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save Quotation
          </button>
        </div>

        {preview && (
          <div data-testid="quote-preview-totals" className="mt-5 bg-royal/50 border border-[#7FB3E8]/40 p-5 flex flex-wrap gap-x-10 gap-y-2">
            <span className="font-mono text-xs tracking-widest uppercase text-white/80">Subtotal <span className="text-white">{fmt(preview.subtotal)}</span></span>
            <span className="font-mono text-xs tracking-widest uppercase text-white/80">GST <span className="text-white">{fmt(preview.gst_amount)}</span></span>
            <span className="font-mono text-xs tracking-widest uppercase text-white/80">Grand Total <span className="text-[#7FB3E8] text-sm">{fmt(preview.grand_total)}</span></span>
            <span className="font-mono text-xs tracking-widest uppercase text-white/60">Weight (internal) {preview.total_kg} kg</span>
          </div>
        )}
      </div>

      {docPreview && (
        <div className="fixed inset-0 z-50 bg-ink/90 overflow-y-auto p-4 md:p-10" data-testid="quote-doc-preview-modal">
          <div className="quote-sheet max-w-3xl mx-auto bg-white text-ink p-8 md:p-12 relative">
            <button data-testid="quote-doc-preview-close" onClick={() => setDocPreview(null)} aria-label="Close" className="no-print absolute top-4 right-4 p-2 text-ink/50 hover:text-ink">
              <X size={22} />
            </button>
            <div className="no-print flex flex-wrap items-center gap-2 mb-6">
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-royal">Preview — not saved yet</p>
              <button data-testid="quote-doc-print-button" onClick={() => window.print()} className="bg-royal text-white font-heading font-semibold uppercase tracking-wider text-sm px-5 py-2.5 hover:bg-ink transition-colors duration-300">
                Print / Save PDF
              </button>
            </div>
            <QuotationSheet quote={docPreview} />
          </div>
        </div>
      )}
    </div>
  );
};
